"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function NavContent({
  isMobile,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-6`}>
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (isMobile) {
    return <div className="flex flex-col gap-2">
        {menuItems.map((item, i) => {
        let href = item.href || "#";
        if (item.label === "Unser Campus") {
          href = "https://excited-caiman.10web.cloud/unser-campus/";
        } else if (item.label === "Über das Zentrum") {
          href = "https://excited-caiman.10web.cloud/ueber-das-zentrum/";
        }
        return <div key={item.id} data-index={i}>
              <Link to={href} onClick={onItemClick} className="block font-default text-lg font-medium text-foreground py-3 border-b border-border/40 hover:text-primary transition-colors uppercase tracking-wider">
                {item.label}
              </Link>
            </div>;
      })}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => {
        let href = item.href || "#";
        if (item.label === "Unser Campus") {
          href = "https://excited-caiman.10web.cloud/unser-campus/";
        } else if (item.label === "Über das Zentrum") {
          href = "https://excited-caiman.10web.cloud/ueber-das-zentrum/";
        }
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={href} className="group relative font-default text-sm font-medium text-foreground transition-colors hover:text-primary py-2 uppercase tracking-widest">
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 h-[1px] w-0 bg-primary transition-all duration-300 -translate-x-1/2 group-hover:w-full"></span>
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="221461" id="navigation" className={`bg-background text-foreground sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-background"}`}>
      <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between relative">
        
        <Link to="/" className="flex items-center shrink-0 z-10 -ml-2 lg:-ml-6 lg:mr-16">
          <div className="flex items-center justify-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1629163/img/3.svg" alt="Resilienzzentrum Soonwald geometric green bird logo" className="h-16 w-auto" loading="lazy" decoding="async" />
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-10 flex-1 justify-end">
          <MenuProvider menu_id="74">
            <NavContent />
          </MenuProvider>
          
          <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 group px-8 h-11 transition-transform hover:scale-105 duration-300">
            <Link to="mailto:post@resilienz-soonwald.de">Kontakt aufnehmen<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:wcs6jq" data-ast-start="5862" data-ast-end="5960"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
          </Button>
        </div>

        <div className="lg:hidden flex items-center z-10">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent/50 rounded-lg" aria-label="Toggle navigation menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-background/95 backdrop-blur-md border-b border-border flex flex-col p-6 gap-6 animate-in slide-in-from-top-2 duration-300">
          <MenuProvider menu_id="74">
            <NavContent isMobile onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          
          <Button asChild className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 group h-12 text-lg mt-4" onClick={() => setIsMobileMenuOpen(false)}>
            <Link to="mailto:post@resilienz-soonwald.de">
              Termin vereinbaren
              <ArrowRight className="ml-2 size-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>}
    </section>;
}